import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { parseContract } from '@/lib/api/parse';

import { parseWarrantyUnitValue } from './format';
import {
  orderWarrantyClaimDetailSchema,
  orderWarrantyClaimListSchema,
  warrantyEligibleItemsSchema,
} from './schemas';
import type {
  ClaimWarrantyValues,
  OrderWarrantyClaimDetail,
  OrderWarrantyClaimList,
  WarrantyEligibleItem,
} from './types';

/**
 * Every Warranty Claim raised against one order, plus the backend's eligibility verdict. One fetch
 * drives routing, the listing rows and the Raise Warranty Claim CTA (ADR-0023). The array is
 * unpaginated and order-scoped, so most-recent-first is a client-side sort — ISO-8601 strings
 * compare lexicographically, so no date parsing is needed.
 */
export async function listOrderWarrantyClaims(orderId: string): Promise<OrderWarrantyClaimList> {
  const path = endpoints.orders.warrantyClaims(orderId);
  const parsed = parseContract(orderWarrantyClaimListSchema, await apiClient.get(path), path);
  parsed.claims.sort((a, b) => b.requestedAt.localeCompare(a.requestedAt));
  return parsed;
}

/** One Warranty Claim's read-only detail, for the Ticket Preview drawer. Nothing to massage — the
 *  reason label is resolved from the client enum map at render time and the Activity Timeline is
 *  derived at render time (ticket 05), so the parsed payload is returned as-is. */
export async function getOrderWarrantyClaim(
  orderId: string,
  claimId: string,
): Promise<OrderWarrantyClaimDetail> {
  const path = endpoints.orders.warrantyClaimDetail(orderId, claimId);
  return parseContract(orderWarrantyClaimDetailSchema, await apiClient.get(path), path);
}

/** Every line item of the order with at least one Unit still eligible for a Warranty Claim, each
 *  carrying its quantity and its gap-preserving `eligibleUnits`. There is no frontend eligibility
 *  logic, deliberately (ADR-0023) — the backend simply omits an ineligible Unit and never returns a
 *  Bundle itself, only one entry per component device sharing the Bundle's `orderItemId`. */
export async function listWarrantyEligibleItems(orderId: string): Promise<WarrantyEligibleItem[]> {
  const path = endpoints.orders.warrantyEligibleItems(orderId);
  return parseContract(warrantyEligibleItemsSchema, await apiClient.get(path), path);
}

/** Raises one Warranty Claim against one **Unit**. The composite option value is split back into
 *  the triple `(orderItemId, unitIndex, bundleDeviceId)` — the third part makes a Bundle's
 *  components individually addressable (ADR-0023). `description` is sent unconditionally:
 *  `zodResolver` hands this function the resolver's **parsed**, already-trimmed output, so there is
 *  no blank case to omit (unlike `createOrderReplacementRequest`). */
export async function createOrderWarrantyClaim(
  orderId: string,
  values: ClaimWarrantyValues,
): Promise<void> {
  const { orderItemId, unitIndex, bundleDeviceId } = parseWarrantyUnitValue(values.device);
  return apiClient.post(endpoints.orders.warrantyClaims(orderId), {
    orderItemId,
    unitIndex,
    bundleDeviceId,
    reason: values.reason,
    description: values.description,
    assetIds: values.images.map((f) => f.id),
  });
}
